import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Trash2, Printer, ScanLine, Search, History, Pencil, X as XIcon } from "lucide-react";
import { Card, Btn, Input } from "@/components/admin/ui";
import { ScannerModal } from "@/components/admin/ScannerModal";
import { API } from "@/config";
import { fmtErr } from "@/context/AuthProvider";

export const Inventory = () => {
  const [units, setUnits] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [scannedUnit, setScannedUnit] = useState(null);
  const [historyUnit, setHistoryUnit] = useState(null);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [editingBarcode, setEditingBarcode] = useState(null);
  const [barcodeDraft, setBarcodeDraft] = useState("");
  const [newUnit, setNewUnit] = useState({ product_id: "", serial_number: "", notes: "" });

  const load = useCallback(async () => {
    const [u, p] = await Promise.all([axios.get(`${API}/admin/inventory`), axios.get(`${API}/admin/products`)]);
    setUnits(u.data.units); setProducts(p.data.products);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onScan = async (code) => {
    setShowScan(false);
    try {
      const { data } = await axios.get(`${API}/admin/inventory/lookup/${encodeURIComponent(code.trim())}`);
      setScannedUnit(data);
      setSearch(code.trim());
      toast.success(`Found: ${data.product_name}`);
    } catch (e) {
      toast.error("No unit found with that barcode");
    }
  };

  const filtered = units.filter(u => (!filter || u.product_id === filter) && (!search || (u.barcode + u.serial_number + u.product_name).toLowerCase().includes(search.toLowerCase())));

  const addUnit = async () => {
    if (!newUnit.product_id) return toast.error("Choose a product");
    try {
      await axios.post(`${API}/admin/inventory`, newUnit);
      toast.success("Unit added");
      setShowAdd(false); setNewUnit({ product_id: "", serial_number: "", notes: "" }); load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const updateStatus = async (id, status) => {
    try { await axios.patch(`${API}/admin/inventory/${id}`, { status }); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this unit? This can't be undone.")) return;
    try { await axios.delete(`${API}/admin/inventory/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const saveBarcode = async (unit) => {
    const next = (barcodeDraft || "").trim();
    if (!next) { toast.error("Barcode can't be empty"); return; }
    if (next === unit.barcode) { setEditingBarcode(null); return; }
    try {
      await axios.patch(`${API}/admin/inventory/${unit.id}`, { barcode: next });
      toast.success("Barcode updated");
      setEditingBarcode(null);
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "That barcode may already exist."); }
  };
  const openHistory = async (unit) => {
    try {
      const { data } = await axios.get(`${API}/admin/inventory/${unit.id}/reservations`);
      setHistoryUnit({ ...data.unit, reservations: data.reservations, product_name: unit.product_name });
    } catch (e) { toast.error("Couldn't load reservation history"); }
  };
  const openProductHistory = async (unit) => {
    try {
      const { data } = await axios.get(`${API}/admin/products/${unit.product_id}/reservations`);
      setHistoryProduct({ product: data.product, reservations: data.reservations });
    } catch (e) { toast.error("Couldn't load product history"); }
  };
  const labelUrl = (id) => `${API}/admin/inventory/${id}/label`;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-serif text-4xl">Inventory</h1>
        <div className="flex items-center gap-2">
          <Btn variant="ghost" onClick={() => setShowScan(true)} data-testid="inventory-scan-btn"><ScanLine className="w-3.5 h-3.5" /> Scan Barcode</Btn>
          <Btn variant="ghost" onClick={() => setShowAdd(!showAdd)} data-testid="inventory-add-toggle"><Plus className="w-3.5 h-3.5" /> Add Unit</Btn>
        </div>
      </div>
      <ScannerModal open={showScan} onClose={() => setShowScan(false)} onResult={onScan} />

      {scannedUnit && (
        <Card className="p-5 mb-4 border-[#FFD166]/50" data-testid="scanned-unit-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="eyebrow">Last scan</div>
              <div className="font-serif text-2xl mt-1">{scannedUnit.product_name}</div>
              <div className="text-xs text-[#B9C8D5]">Barcode: <span className="text-[#FFD166]">{scannedUnit.barcode}</span> · Status: <span className="text-[#F6F1E4]">{scannedUnit.status}</span></div>
            </div>
            <div className="flex items-center gap-2">
              <select value={scannedUnit.status} onChange={async (e) => { await axios.patch(`${API}/admin/inventory/${scannedUnit.id}`, { status: e.target.value }); setScannedUnit({ ...scannedUnit, status: e.target.value }); load(); }} className="bg-black/30 border border-[#FFD166]/25 rounded-lg px-3 py-1.5 text-sm" data-testid="scanned-unit-status">
                <option value="available">Available</option>
                <option value="rented">Rented</option>
                <option value="maintenance">Maintenance</option>
                <option value="retired">Retired</option>
              </select>
              <Btn variant="ghost" onClick={() => setScannedUnit(null)}>Clear</Btn>
            </div>
          </div>
        </Card>
      )}

      {showAdd && (
        <Card className="p-5 mb-4">
          <div className="grid md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#B9C8D5]">Product</label>
              <select className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg mt-1" value={newUnit.product_id} onChange={e => setNewUnit({ ...newUnit, product_id: e.target.value })} data-testid="new-unit-product">
                <option value="">Choose…</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#B9C8D5]">Serial # (optional)</label>
              <Input value={newUnit.serial_number} onChange={e => setNewUnit({ ...newUnit, serial_number: e.target.value })} data-testid="new-unit-serial" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#B9C8D5]">Notes</label>
              <Input value={newUnit.notes} onChange={e => setNewUnit({ ...newUnit, notes: e.target.value })} />
            </div>
            <Btn onClick={addUnit} data-testid="new-unit-save">Create + Auto Barcode</Btn>
          </div>
        </Card>
      )}

      <Card className="p-4 mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-[#B9C8D5]" />
          <input placeholder="Search barcode / serial / product…" className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] pl-10 pr-3 py-2 rounded-lg" value={search} onChange={e => setSearch(e.target.value)} data-testid="inventory-search" />
        </div>
        <select className="bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg" value={filter} onChange={e => setFilter(e.target.value)} data-testid="inventory-filter">
          <option value="">All products</option>
          {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Card>

      <div className="space-y-2">
        {filtered.map(u => (
          <Card key={u.id} className="p-4" data-testid={`unit-row-${u.id}`}>
            <div className="grid md:grid-cols-[1fr_auto_auto_auto_auto] gap-3 items-center">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFD166]">{u.product_category}</div>
                <button
                  type="button"
                  onClick={() => openProductHistory(u)}
                  className="font-serif text-lg text-left hover:text-[#FFD166] transition-colors inline-flex items-center gap-1 group"
                  title="Show every reservation that booked this product"
                  data-testid={`unit-product-link-${u.id}`}
                >
                  {u.product_name}
                  <History className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                {editingBarcode === u.id ? (
                  <div className="mt-1 flex items-center gap-1.5" data-testid={`unit-barcode-edit-${u.id}`}>
                    <Input
                      value={barcodeDraft}
                      onChange={(e) => setBarcodeDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveBarcode(u); if (e.key === "Escape") setEditingBarcode(null); }}
                      autoFocus
                      className="text-xs h-7 py-0"
                      data-testid={`unit-barcode-input-${u.id}`}
                    />
                    <button type="button" onClick={() => saveBarcode(u)} className="text-[#7EE787] text-xs px-2 py-1 hover:bg-emerald-500/10 rounded" data-testid={`unit-barcode-save-${u.id}`}>save</button>
                    <button type="button" onClick={() => setEditingBarcode(null)} className="text-[#B9C8D5] text-xs px-2 py-1 hover:bg-black/30 rounded"><XIcon className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <div className="text-xs text-[#B9C8D5] mt-0.5">
                    Barcode: <button type="button" onClick={() => { setEditingBarcode(u.id); setBarcodeDraft(u.barcode); }} className="text-[#F6F1E4] hover:text-[#FFD166] inline-flex items-center gap-1 group" data-testid={`unit-barcode-${u.id}`}>
                      {u.barcode}
                      <Pencil className="w-2.5 h-2.5 opacity-40 group-hover:opacity-100" />
                    </button>
                    {u.serial_number ? ` · Serial ${u.serial_number}` : ""}
                  </div>
                )}
              </div>
              <select value={u.status} onChange={e => updateStatus(u.id, e.target.value)} className={`bg-black/30 border rounded-lg px-3 py-1.5 text-sm ${u.status === "available" ? "border-emerald-500/40 text-emerald-300" : u.status === "rented" ? "border-orange-500/40 text-orange-300" : "border-red-500/40 text-red-300"}`} data-testid={`unit-status-${u.id}`}>
                <option value="available">Available</option>
                <option value="rented">Rented</option>
                <option value="maintenance">Maintenance</option>
                <option value="retired">Retired</option>
              </select>
              <select
                value={u.condition || "good"}
                onChange={async (e) => { await axios.patch(`${API}/admin/inventory/${u.id}`, { condition: e.target.value }); load(); }}
                className={`bg-black/30 border rounded-lg px-3 py-1.5 text-sm ${(u.condition || "good") === "good" ? "border-emerald-500/30 text-emerald-200" : (u.condition === "damaged") ? "border-red-500/50 text-red-300" : "border-amber-500/50 text-amber-300"}`}
                data-testid={`unit-condition-${u.id}`}
                title="Physical condition — damaged / out-of-service units are excluded from availability."
              >
                <option value="good">✓ Good</option>
                <option value="damaged">⚠ Damaged</option>
                <option value="out_of_service">⊘ Out of service</option>
              </select>
              <Btn variant="ghost" onClick={() => openHistory(u)} data-testid={`unit-history-${u.id}`}><History className="w-3.5 h-3.5" /> History</Btn>
              <a href={labelUrl(u.id)} target="_blank" rel="noreferrer"><Btn variant="ghost" data-testid={`unit-label-${u.id}`}><Printer className="w-3.5 h-3.5" /> Label</Btn></a>
              <Btn variant="danger" onClick={() => del(u.id)}><Trash2 className="w-3.5 h-3.5" /></Btn>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <div className="text-center text-[#B9C8D5] py-10">No units match.</div>}
      </div>

      {historyUnit && <UnitHistoryModal unit={historyUnit} onClose={() => setHistoryUnit(null)} />}
      {historyProduct && (
        <UnitHistoryModal
          unit={{
            product_name: historyProduct.product.name,
            barcode: `All units of "${historyProduct.product.name}"`,
            reservations: historyProduct.reservations,
          }}
          onClose={() => setHistoryProduct(null)}
        />
      )}
    </div>
  );
};

const UnitHistoryModal = ({ unit, onClose }) => {
  const reservations = unit.reservations || [];
  const today = new Date().toISOString().slice(0, 10);
  const bucket = (r) => {
    if (r.status === "cancelled") return "cancelled";
    if ((r.pickup_date || "") < today) return "past";
    if ((r.delivery_date || "") > today) return "future";
    return "current";
  };
  const bucketLabel = { current: "Currently rented", future: "Upcoming", past: "Past", cancelled: "Cancelled" };
  const groups = ["current", "future", "past", "cancelled"].map((k) => ({
    key: k,
    label: bucketLabel[k],
    rows: reservations.filter((r) => bucket(r) === k),
  }));
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-end p-4" onClick={onClose} data-testid="unit-history-modal">
      <div className="glass w-full max-w-xl max-h-[92vh] overflow-y-auto p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#FFD166]" data-testid="unit-history-close"><XIcon className="w-5 h-5" /></button>
        <p className="eyebrow text-[#FFD166]">Reservation history</p>
        <h2 className="font-serif text-2xl mt-1">{unit.product_name}</h2>
        <div className="text-xs text-[#B9C8D5] mt-0.5">Barcode: <span className="text-[#F6F1E4]">{unit.barcode}</span> · {reservations.length} total booking{reservations.length === 1 ? "" : "s"}</div>

        {reservations.length === 0 ? (
          <p className="text-[#B9C8D5]/70 italic mt-6" data-testid="unit-history-empty">This unit has never been assigned to a reservation.</p>
        ) : (
          <div className="mt-6 space-y-6">
            {groups.filter(g => g.rows.length > 0).map((g) => (
              <div key={g.key}>
                <div className="eyebrow mb-2 flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${g.key === "current" ? "bg-emerald-400" : g.key === "future" ? "bg-[#FFD166]" : g.key === "past" ? "bg-[#B9C8D5]" : "bg-red-400"}`} />
                  {g.label} ({g.rows.length})
                </div>
                <div className="space-y-2">
                  {g.rows.map((r) => (
                    <Link
                      key={r.id}
                      to={`/admin/reservations`}
                      state={{ focusId: r.id }}
                      className="block rounded-lg border border-[#FFD166]/15 bg-black/30 hover:border-[#FFD166]/50 p-3 transition-colors"
                      data-testid={`unit-history-row-${r.id}`}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-[#F6F1E4] font-medium truncate">{r.customer_name}</div>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${r.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-300" : "bg-orange-500/15 text-orange-300"}`}>{r.payment_status}</span>
                      </div>
                      <div className="text-[11px] text-[#B9C8D5] mt-0.5">
                        {r.delivery_date} → {r.pickup_date} · {r.delivery_resort}
                      </div>
                      <div className="text-[10px] text-[#B9C8D5]/60 mt-0.5 font-mono">{r.id.slice(0, 8)}…</div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
